import { useCallback, useEffect, useState } from "react";

/**
 * useListOrdering — Wave 25.13 (decision 1c).
 *
 * Per-user, per-list ordering rules:
 *   1. Pinned items always first (in pin order).
 *   2. Then current user's assigned items.
 *   3. Then any items the user has manually moved up/down within their
 *      group, preserving the user's chosen order.
 *   4. Default sort fills the rest (e.g. scheduled_at desc).
 *
 * State lives in localStorage keyed by `<storageKey>:<userId>` so every
 * device the user logs into picks up their own ordering. Note: this is
 * a per-device cache today — Wave 25.x can promote it to a server-side
 * `user_list_prefs` collection if cross-device sync becomes important.
 *
 * @param {string} storageKey  e.g. "jobcards.order"
 * @param {string} userId      current user id
 *
 * Returned helpers:
 *   pinned: Set<id>           ids that are pinned to top
 *   isPinned(id) -> bool
 *   togglePin(id) -> void
 *   orderMap: Record<groupKey, string[]>   manual order per group
 *   moveUp(groupKey, id, allIds) -> void
 *   moveDown(groupKey, id, allIds) -> void
 *   sortItems({ items, groupKey, isMine }) -> sorted array
 */
export function useListOrdering(storageKey, userId) {
  const pinKey = `${storageKey}.pins:${userId || "anon"}`;
  const orderKey = `${storageKey}.order:${userId || "anon"}`;

  const [pinned, setPinned] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(pinKey) || "[]")); }
    catch { return new Set(); }
  });
  const [orderMap, setOrderMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(orderKey) || "{}"); }
    catch { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem(pinKey, JSON.stringify([...pinned])); } catch (_) { /* quota */ }
  }, [pinned, pinKey]);
  useEffect(() => {
    try { localStorage.setItem(orderKey, JSON.stringify(orderMap)); } catch (_) { /* quota */ }
  }, [orderMap, orderKey]);

  const togglePin = useCallback((id) => {
    setPinned((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const isPinned = useCallback((id) => pinned.has(id), [pinned]);

  const moveUp = useCallback((groupKey, id, allIds) => {
    setOrderMap((prev) => {
      const current = Array.isArray(prev[groupKey]) && prev[groupKey].length
        ? [...prev[groupKey]]
        : [...allIds];
      // ensure id is in the list
      if (!current.includes(id)) current.push(id);
      const idx = current.indexOf(id);
      if (idx <= 0) return prev;
      [current[idx - 1], current[idx]] = [current[idx], current[idx - 1]];
      return { ...prev, [groupKey]: current };
    });
  }, []);

  const moveDown = useCallback((groupKey, id, allIds) => {
    setOrderMap((prev) => {
      const current = Array.isArray(prev[groupKey]) && prev[groupKey].length
        ? [...prev[groupKey]]
        : [...allIds];
      if (!current.includes(id)) current.push(id);
      const idx = current.indexOf(id);
      if (idx === -1 || idx >= current.length - 1) return prev;
      [current[idx + 1], current[idx]] = [current[idx], current[idx + 1]];
      return { ...prev, [groupKey]: current };
    });
  }, []);

  /**
   * Sort items in a group:
   *   1. Pinned (in pin order)
   *   2. items in manual order map
   *   3. user's-own + in-progress first within remaining
   *   4. fall through to original ordering of `items`
   *
   *   `isMine` (optional) → boolean predicate
   *   `isHot`  (optional) → boolean predicate (e.g. JC.phase === "in_progress")
   */
  const sortItems = useCallback(({ items, groupKey, isMine, isHot }) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    const pinList = [...pinned];
    const pinSet = new Set(pinList);
    const manualOrder = orderMap[groupKey] || [];
    const manualSet = new Set(manualOrder);
    const byId = new Map(items.map((it) => [it.id, it]));

    const out = [];
    const used = new Set();

    // 1. Pinned (only those present in items)
    for (const pid of pinList) {
      if (byId.has(pid)) {
        out.push(byId.get(pid));
        used.add(pid);
      }
    }
    // 2. Manually ordered (excluding pinned)
    for (const oid of manualOrder) {
      if (used.has(oid)) continue;
      if (byId.has(oid)) {
        out.push(byId.get(oid));
        used.add(oid);
      }
    }
    // 3. Unordered — Wave 26.9 — score by priority then keep original
    //    relative order. Score (higher = closer to top):
    //     +2  user's own
    //     +1  hot / in-progress
    //    Combined so a tech sees (own & in-progress) first, then own,
    //    then anyone-else's in-progress, then everything else.
    const remaining = items.filter((it) => !used.has(it.id) && !pinSet.has(it.id) && !manualSet.has(it.id));
    const score = (it) => (isMine?.(it) ? 2 : 0) + (isHot?.(it) ? 1 : 0);
    // Stable sort preserves the original order within equal scores.
    const indexed = remaining.map((it, idx) => ({ it, idx, s: score(it) }));
    indexed.sort((a, b) => b.s - a.s || a.idx - b.idx);
    out.push(...indexed.map((x) => x.it));
    return out;
  }, [pinned, orderMap]);

  return { pinned, isPinned, togglePin, orderMap, moveUp, moveDown, sortItems };
}
